import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";

const STATUS_LABELS = { available: "Disponible", reserved: "Reservada", rented: "Alquilada", sold: "Vendida", inactive: "Inactiva" };
const PUBLIC_PROPERTY_STATUSES = new Set(["available", "reserved"]);

const EMPTY = {
  code: "",
  title: "",
  description: "",
  operation: "rent",
  status: "available",
  propertyType: "departamento",
  price: "",
  currency: "ARS",
  expenses: "",
  bedrooms: "",
  bathrooms: "",
  area: "",
  publicVisible: true,
  address: { street: "", number: "", floor: "", unit: "", city: "", province: "", postalCode: "", country: "Argentina" },
  amenitiesText: "",
  imagesText: "",
};

function toForm(property) {
  if (!property) return EMPTY;
  return {
    ...EMPTY,
    ...property,
    code: property.code || "",
    title: property.title || "",
    description: property.description || "",
    price: property.price ?? "",
    expenses: property.expenses ?? "",
    bedrooms: property.bedrooms ?? "",
    bathrooms: property.bathrooms ?? "",
    area: property.area ?? "",
    address: { ...EMPTY.address, ...(property.address || {}) },
    amenitiesText: Array.isArray(property.amenities) ? property.amenities.join(", ") : "",
    imagesText: Array.isArray(property.images)
      ? property.images.map((image) => image?.path || image?.url || image).filter(Boolean).join("\n")
      : "",
  };
}

function nullableNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  return Number(value);
}

export default function PropertyDialog({ open, property, onClose, onSave, saving = false }) {
  const [form, setForm] = React.useState(EMPTY);

  React.useEffect(() => {
    if (open) setForm(toForm(property));
  }, [open, property]);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const setAddress = (key) => (event) => setForm((current) => ({ ...current, address: { ...current.address, [key]: event.target.value } }));
  const hiddenByStatus = Boolean(form.publicVisible) && !PUBLIC_PROPERTY_STATUSES.has(form.status);
  const hiddenStatusReason = STATUS_LABELS[form.status]
    ? `mientras esté ${STATUS_LABELS[form.status].toLowerCase()}`
    : "por su estado actual";

  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      code: form.code.trim() || undefined,
      title: form.title.trim(),
      description: form.description.trim(),
      operation: form.operation,
      status: form.status,
      propertyType: form.propertyType.trim(),
      address: form.address,
      price: Number(form.price),
      currency: form.currency.trim().toUpperCase(),
      expenses: nullableNumber(form.expenses),
      bedrooms: nullableNumber(form.bedrooms),
      bathrooms: nullableNumber(form.bathrooms),
      area: nullableNumber(form.area),
      publicVisible: Boolean(form.publicVisible),
      amenities: form.amenitiesText.split(",").map((value) => value.trim()).filter(Boolean),
      images: form.imagesText.split(/\r?\n/).map((value) => value.trim()).filter(Boolean),
    };
    await onSave(payload);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <Box component="form" onSubmit={submit}>
        <DialogTitle sx={{ fontWeight: 950 }}>{property ? "Editar propiedad" : "Nueva propiedad"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Código" value={form.code} onChange={set("code")} helperText="Opcional y único dentro del proyecto." /></Grid>
            <Grid item xs={12} sm={8}><TextField fullWidth required label="Título" value={form.title} onChange={set("title")} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Descripción" value={form.description} onChange={set("description")} /></Grid>
            <Grid item xs={12} sm={4}><TextField select fullWidth label="Operación" value={form.operation} onChange={set("operation")}><MenuItem value="rent">Alquiler</MenuItem><MenuItem value="sale">Venta</MenuItem><MenuItem value="both">Venta o alquiler</MenuItem></TextField></Grid>
            <Grid item xs={12} sm={4}><TextField select fullWidth label="Estado" value={form.status} onChange={set("status")}><MenuItem value="available">Disponible</MenuItem><MenuItem value="reserved">Reservada</MenuItem><MenuItem value="rented">Alquilada</MenuItem><MenuItem value="sold">Vendida</MenuItem><MenuItem value="inactive">Inactiva</MenuItem></TextField></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth required label="Tipo de propiedad" value={form.propertyType} onChange={set("propertyType")} placeholder="departamento, casa, local..." /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth required type="number" inputProps={{ min: 0, step: "any" }} label="Precio" value={form.price} onChange={set("price")} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth label="Moneda" inputProps={{ maxLength: 3 }} value={form.currency} onChange={set("currency")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type="number" inputProps={{ min: 0, step: "any" }} label="Expensas" value={form.expenses} onChange={set("expenses")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type="number" inputProps={{ min: 0 }} label="Dormitorios" value={form.bedrooms} onChange={set("bedrooms")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type="number" inputProps={{ min: 0, step: "any" }} label="Baños" value={form.bathrooms} onChange={set("bathrooms")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type="number" inputProps={{ min: 0, step: "any" }} label="Superficie (m²)" value={form.area} onChange={set("area")} /></Grid>
            <Grid item xs={12}><Box sx={{ fontWeight: 900, color: "var(--wp-panel-text-primary, #19242F)", mt: 1 }}>Ubicación pública</Box></Grid>
            <Grid item xs={12} sm={8}><TextField fullWidth label="Calle" value={form.address.street} onChange={setAddress("street")} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Número" value={form.address.number} onChange={setAddress("number")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Piso" value={form.address.floor} onChange={setAddress("floor")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Unidad" value={form.address.unit} onChange={setAddress("unit")} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Localidad" value={form.address.city} onChange={setAddress("city")} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Provincia" value={form.address.province} onChange={setAddress("province")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Código postal" value={form.address.postalCode} onChange={setAddress("postalCode")} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="País" value={form.address.country} onChange={setAddress("country")} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Amenities" value={form.amenitiesText} onChange={set("amenitiesText")} helperText="Separalos por coma: cochera, patio, pileta..." /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Imágenes" value={form.imagesText} onChange={set("imagesText")} helperText="Una URL o ruta de media por línea. Se conserva el formato de media del proyecto." /></Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={Boolean(form.publicVisible)} onChange={(event) => setForm((current) => ({ ...current, publicVisible: event.target.checked }))} />} label="Publicar en el catálogo web" />
              {hiddenByStatus ? <Alert severity="info" sx={{ mt: 0.5 }}>La propiedad no se mostrará en la web {hiddenStatusReason}. La preferencia de publicación queda guardada.</Alert> : null}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={onClose} disabled={saving}>Cancelar</Button><Button type="submit" variant="contained" disabled={saving || !form.title.trim() || !form.propertyType.trim() || form.price === ""}>{saving ? "Guardando..." : "Guardar"}</Button></DialogActions>
      </Box>
    </Dialog>
  );
}
